import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AIDashboard.css';

// Use production API URL if available, otherwise localhost for development
const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
    ? 'http://localhost:5001/api' 
    : 'https://your-backend-url.com/api'); // Update this with your deployed backend URL

function AIDashboard() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false); // Start as false since we don't auto-load
  const [error, setError] = useState(null);
  const [useMock, setUseMock] = useState(false);
  const [apiStats, setApiStats] = useState(null);

  // Don't auto-fetch on load - only when user clicks refresh
  // useEffect(() => {
  //   fetchCompanies();
  //   const interval = setInterval(fetchCompanies, 60000);
  //   return () => clearInterval(interval);
  // }, [useMock]);

  const fetchApiStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/stats`);
      setApiStats(response.data);
    } catch (err) {
      console.error('Failed to fetch API stats:', err);
    }
  };

  useEffect(() => {
    fetchApiStats();
    // Update stats every 30 seconds
    const statsInterval = setInterval(fetchApiStats, 30000);
    return () => clearInterval(statsInterval);
  }, []);

  const fetchCompanies = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const params = useMock ? { mock: 'true' } : {};
      if (forceRefresh) {
        params.refresh = 'true'; // Force API refresh instead of using cache
      }
      const response = await axios.get(`${API_URL}/ai/companies`, { 
        params,
        timeout: 120000 // 2 minute timeout for AI companies (takes longer)
      });
      setCompanies(response.data.companies || []);
      fetchApiStats(); // Update stats after fetch
    } catch (err) {
      if (err.code === 'ECONNABORTED') {
        setError('Request timed out. The backend is processing many companies. Try using mock data or wait longer.');
      } else if (err.code === 'ERR_NETWORK' || err.message.includes('ERR_EMPTY_RESPONSE')) {
        setError('Cannot connect to backend. Make sure the backend is running on port 5001.');
      } else {
        setError('Failed to fetch AI companies data. Make sure the backend is running and Yahoo Finance API is accessible.');
      }
      console.error('API Error:', err);
      setCompanies([]); // Clear on error, don't use mock automatically
    } finally {
      setLoading(false);
    }
  };

  const getBullishColor = (percentage) => {
    if (percentage >= 70) return '#10b981'; // green
    if (percentage >= 50) return '#f59e0b'; // yellow
    if (percentage >= 30) return '#ef4444'; // red
    return '#6b7280'; // gray
  };

  const getTimingColor = (timing) => {
    switch (timing) {
      case 'invest_now': return '#10b981';
      case 'invest_soon': return '#f59e0b';
      case 'wait': return '#6b7280';
      case 'avoid': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getTimingIcon = (timing) => {
    switch (timing) {
      case 'invest_now': return '🚀';
      case 'invest_soon': return '⏰';
      case 'wait': return '⏳';
      case 'avoid': return '🚫';
      default: return '➡️';
    }
  };

  if (loading) {
    return (
      <div className="ai-dashboard">
        <div className="loading">Loading AI companies...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ai-dashboard">
        <div className="error-message">{error}</div>
        <button onClick={() => setUseMock(true)} className="btn btn-primary">
          Use Mock Data
        </button>
      </div>
    );
  }

  return (
    <div className="ai-dashboard">
      <div className="ai-header">
        <h1>🤖 AI & LLM Investment Tracker</h1>
        <p>Track top 20 AI companies • Model announcements • Investment timing</p>
        <div className="header-controls">
              <button 
            onClick={() => fetchCompanies(false)} 
            className="btn btn-secondary"
            title="Load from cache (no API calls)"
            disabled={loading}
          >
            📥 Load Cached
          </button>
          <button 
            onClick={() => fetchCompanies(true)} 
            className="btn btn-primary"
            title="Force refresh from API (uses API calls)"
          >
            🔄 Update from API
          </button>
          <button 
            onClick={() => setUseMock(!useMock)} 
            className={`btn ${useMock ? 'btn-active' : 'btn-secondary'}`}
          >
            {useMock ? '✅ Mock Mode' : '🎭 Use Mock'}
          </button>
        </div>
        {apiStats && (
          <div className="api-stats">
            <div className="stat-item">
              <span className="stat-label">FMP API Calls:</span>
              <span className="stat-value">
                {apiStats.fmp_api_calls_used} / {apiStats.fmp_api_limit} used
                <span className="stat-remaining"> ({apiStats.fmp_api_calls_remaining} remaining)</span>
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Cached Symbols:</span>
              <span className="stat-value">{apiStats.cached_symbols}</span>
            </div>
          </div>
        )}
      </div>

      <div className="companies-grid">
        {companies.map((company, index) => {
          const bullishPct = company.bullish_percentage || 50;
          const timing = company.investment_timing || {};
          const announcement = company.announcement || {};
          const prediction = company.prediction || {};

          return (
            <div key={company.symbol} className="company-card">
              <div className="company-rank">#{index + 1}</div>
              
              <div className="company-header">
                <div>
                  <h2>{company.symbol}</h2>
                  <p className="company-name">{company.name}</p>
                  <p className="company-focus">{company.focus}</p>
                </div>
                <div className="price-info">
                  <div className="price">${company.current_price?.toFixed(2)}</div>
                  <div className={`price-change ${company.price_change >= 0 ? 'positive' : 'negative'}`}>
                    {company.price_change >= 0 ? '+' : ''}
                    {company.price_change?.toFixed(2)} ({company.price_change_percent >= 0 ? '+' : ''}
                    {company.price_change_percent?.toFixed(2)}%)
                  </div>
                </div>
              </div>

              <div className="bullish-meter">
                <div className="bullish-header">
                  <span className="bullish-label">Bullish</span>
                  <span 
                    className="bullish-percentage"
                    style={{ color: getBullishColor(bullishPct) }}
                  >
                    {bullishPct}%
                  </span>
                </div>
                <div className="bullish-bar">
                  <div 
                    className="bullish-fill"
                    style={{ 
                      width: `${bullishPct}%`,
                      backgroundColor: getBullishColor(bullishPct)
                    }}
                  />
                </div>
              </div>

              <div 
                className="investment-timing"
                style={{ borderLeftColor: getTimingColor(timing.timing) }}
              >
                <div className="timing-header">
                  <span className="timing-icon">{getTimingIcon(timing.timing)}</span>
                  <span 
                    className="timing-status"
                    style={{ color: getTimingColor(timing.timing) }}
                  >
                    {timing.message || 'Analyzing...'}
                  </span>
                </div>
                {timing.days !== null && timing.days !== undefined && (
                  <div className="timing-days">
                    {timing.days === 0 ? 'Now' : `In ${timing.days} day${timing.days > 1 ? 's' : ''}`}
                  </div>
                )}
              </div>

              {announcement.has_announcement && (
                <div className="announcement-badge">
                  <span className="announcement-icon">📢</span>
                  <span className="announcement-text">New Model Announcement!</span>
                  {announcement.text && (
                    <div className="announcement-detail">{announcement.text}</div>
                  )}
                </div>
              )}

              <div className="company-stats">
                <div className="stat-item">
                  <span className="stat-label">Prediction:</span>
                  <span className={`stat-value prediction-${prediction.prediction || 'neutral'}`}>
                    {(prediction.prediction || 'neutral').toUpperCase()}
                    {prediction.confidence && ` ${prediction.confidence}%`}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Mentions:</span>
                  <span className="stat-value">
                    {company.mentions?.total || 0} 
                    ({company.mentions?.positive || 0}👍 {company.mentions?.negative || 0}👎)
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {companies.length === 0 && (
        <div className="no-results">
          No AI companies found. Try enabling mock data.
        </div>
      )}
    </div>
  );
}

export default AIDashboard;

